// Builds the engine Catalog from live sources (WAT-11276 Phase 2).
//
// Sources (see the packages-config + epoch cache pipeline):
//   - package master data: the Storyblok "Packages Config" story
//     (cache key globalStoryblok:packagesConfig -> .packageConfig, IPackageConfig[]
//     in lib/types/global.ts — id = canonical ATT_TV_* code, type "genre-mini" = mini,
//     queryparam = shop-facing code)
//   - prices: the already-populated epoch offer caches in the
//     genreSvodOffers.offerPrices shape ({ [ATT_TV_id]: { price, ... } }[],
//     built by lib/cache/resolver/epoch-genre-offers.resolver.ts)
//
// Prices are NEVER read from CMS copy. A missing/zero/negative price becomes null:
// the engine's null-propagation then skips the bundle-guard and no $0 card can
// render. Callers must treat any 'error' message as "do not render the component".

// Embedded-SVOD and genre-SVOD names flow from the Epoch caches into user-facing
// copy verbatim, so the two `.map(decodeHtmlEntities(...))` sites below decode them.
// This was a local numeric-only decoder until 2026-09-03, when a live payload sweep
// found named forms ("DIRECTV&reg; Deportes", "EN ESPA&Ntilde;OL&trade;",
// "&Oacute;PTIMO M&Aacute;S") its comment had claimed never appear in the cache.
//
// Imported relative for the same reason as the `safeHttpUrl` import below — the
// out-of-repo recommendation-freeze gate bundles this module with an esbuild config
// that has no path aliases, so `lib/helper/...` would break the gate. The decoder
// itself is dependency-free, so adding it to that bundle costs nothing.
import { decodeHtmlEntities } from '../../../../../lib/helper/decode-html-entities';
import { Catalog, CatalogGenrePack, CatalogPackage, Recipe } from '../engine/engine.types';
import { RecipeValidationMessage } from '../engine/validate-recipe';
// Relative, not the `src/utils/...` form the other callers use: the recommendation-freeze gate
// bundles this module with an esbuild config that has no path aliases, so an alias-shaped import
// breaks the gate rather than the app. That gate lives outside this repo (see the worktree
// HANDOFF), which is why nothing here can enforce it.
// Safe on the server: UtilityFunctions has no top-level imports and no module-scope browser globals.
// Not client-free though — the editor-preview chunk value-imports adaptCatalog via
// useStoryblokLivePage -> page-adapter, so both modules do ship there.
import { safeHttpUrl } from '../../../../utils/UtilityFunctions';

export interface PackageConfigEntry {
  id?: string;
  type?: string;
  queryparam?: string;
  displayLabel?: string;
  epochPackageName?: string;
  billingProductCode?: string;
  order?: string | number;
  /**
   * Authored channel count. The parts stay separate rather than pre-joined because
   * that is how the CMS authors them and how every other reader of this same field
   * consumes them: `displayCount` is the marketing figure (it can differ from the
   * literal `count`), `signLetter` is the "+" and `label` is the authored noun.
   * The same three-field join lives in `lib/adapters/storyblok/package-compare-tool-adapeter.ts:105`
   * and `lib/adapters/storyblok/genre-preview-tool-adapter.ts:52`; the
   * `displayCount` -> `count` fallback comes from
   * `src/components/custom-acq-compare-tool/utils/getChannels.ts:229`.
   * (plan-card, `lib/adapters/storyblok/plan-card-adapter.ts:22-33`, reads only two
   * of them — displayCount + signLetter — so it is not the precedent for the join.)
   *
   * Storyblok serialises a bloks field as an array, and `flip-cards-adapter.ts:304`
   * already defends against exactly that on this same key, so accept both shapes.
   */
  channel?: PackageConfigChannel | PackageConfigChannel[] | null;
}

/** One authored channel block inside `PackageConfigEntry.channel`. */
export interface PackageConfigChannel {
  count?: string | number | null;
  displayCount?: string | number | null;
  label?: string | null;
  signLetter?: string | null;
}

export interface EpochOfferPriceEntry {
  [attTvId: string]: {
    price?: number | string | null;
    regularPrice?: number | string | null;
    promoDuration?: number | string | null;
    nextTier?: NextPriceTier | null;
    /**
     * false = the source could not tell whether `price` is a promo. Only the dtvi
     * bridge sets it, and only when the benefits array it derives from was missing;
     * everything else omits it and is trusted (the genre cache computes the field
     * itself, so its null genuinely means "no promo").
     */
    promoKnown?: boolean;
  };
}

/**
 * What `price` becomes once the shortest promo tier expires, and the months that
 * second price covers ({price: 84.99, startMo: 2, endMo: 24} = "$84.99/mo. for mos
 * 2-24"). Only the dtvi signature cache can express this — it is the only one that
 * ships the per-tier benefits. Genre packs stop at promoDuration.
 */
export interface NextPriceTier {
  price?: number | string | null;
  startMo?: number | string | null;
  endMo?: number | string | null;
}

export interface EmbeddedSvodOfferEntry {
  code?: string;
  name?: string;
}

/**
 * CatalogPackage plus the promo length behind `price`, in months. Kept out of the
 * engine's CatalogPackage on purpose: the engine ranks on price alone and never
 * reads this — it exists so the result card can disclose "for first 2 mos".
 * null means no live promo, i.e. `price` is the ongoing rate.
 */
export interface PricedCatalogPackage extends CatalogPackage {
  promoDuration?: number | null;
  nextTier?: ResolvedPriceTier | null;
  promoKnown?: boolean;
  /**
   * Rendered channel-count line ("125+ channels"); null when unauthored. Display only.
   *
   * This replaces `CatalogPackage.publishedCount`, which declared the same concept on the
   * engine type and was populated in GOLDEN_CATALOG but written and read by nothing. That
   * field is deleted in the same change — two names for one value is how the next reader
   * wires the card to the dead one.
   */
  channels?: string | null;
}

/** Genre-pack flavour of PricedCatalogPackage. */
export interface PricedCatalogGenrePack extends CatalogGenrePack {
  promoDuration?: number | null;
  nextTier?: ResolvedPriceTier | null;
  promoKnown?: boolean;
  /** Rendered channel-count line ("125+ channels"); null when unauthored. Display only. */
  channels?: string | null;
}

/** NextPriceTier after validation — every field is a usable positive integer. */
export interface ResolvedPriceTier {
  price: number;
  startMo: number;
  endMo: number;
}

/**
 * What adaptCatalog actually returns. The engine only needs `Catalog`, but returning
 * the narrower type would erase the display fields at the one boundary where they are
 * provably present and force the UI to cast a plain Catalog back up — a cast that
 * compiles for ANY Catalog (a fixture, another producer) and silently yields
 * undefined. Declaring it here keeps those reads type-checked.
 */
export interface PricedCatalog extends Catalog {
  signatures: PricedCatalogPackage[];
  spanishSignature: PricedCatalogPackage;
  genrePacks: PricedCatalogGenrePack[];
}

/**
 * One authored CTA-URL row from the recommender blok's `catalog` bloks field. The CMS adapter only
 * renames `pack_code` / `cta_url`, so every value here is UNVALIDATED CMS input.
 *
 * Both fields are `unknown` rather than `string?` on purpose: a hand-edited blok can carry a nested
 * blok or a number in either field, and if the type denies that, the guards in adaptCatalog read as
 * dead code and get deleted.
 *
 * Raw rows rather than a `Record<packCode, url>` — a Record cannot express a duplicate `pack_code`,
 * and adaptCatalog is where every config warning is reported.
 */
export interface CtaUrlOverride {
  /** Canonical package id (ATT_TV_*) this URL belongs to — the blok's `pack_code` */
  packCode?: unknown;
  /** Authored BuyFlow URL — the blok's `cta_url`. Blank means "use the derived URL". */
  ctaUrl?: unknown;
}

export interface CatalogSources {
  packageConfig?: PackageConfigEntry[] | null;
  offerPrices?: EpochOfferPriceEntry[] | null;
  /**
   * CMS-authored per-product CTA URLs (the recommender blok's `catalog`). Absent on any story
   * authored before the field existed, which is why every reader treats it as optional.
   * Validated in adaptCatalog, not by the caller.
   */
  ctaOverrides?: CtaUrlOverride[] | null;
  /**
   * epoch dtviEmbeddedSvodOffers: lowercase shop queryparam ('premier', 'choice', 'optimomas'…)
   * -> streaming subscriptions embedded in that DTVI signature. DTVI-only by construction —
   * there is no BYOD equivalent of this cache, which is why any svod-inclusion copy carries
   * the "with a DIRECTV device" condition.
   */
  embeddedSvodOffers?: Record<string, EmbeddedSvodOfferEntry[] | null | undefined> | null;
  /** epoch genreSvodOffers.svodMapping: canonical ATT_TV_* genre id -> embedded SVOD offer codes */
  genreSvodMapping?: Record<string, string[] | null | undefined> | null;
  /** epoch genreSvodOffers.svodNameMapping: SVOD offer code -> display name */
  svodNameMapping?: Record<string, string | null | undefined> | null;
}

export interface AdaptedCatalog {
  catalog: PricedCatalog | null;
  messages: RecipeValidationMessage[];
}

// Structural product facts: WHICH tier is which — never prices. The signature
// ladder and premium-SVOD embedding change with product structure, not offers;
// re-source from the packages-config story if it grows fields for them.
const SIGNATURE_RANKS: Record<string, number> = {
  ATT_TV_ENTERTAINMENT: 1,
  ATT_TV_CHOICE: 2,
  ATT_TV_ULTIMATE: 3,
  ATT_TV_PREMIER: 4
};
// Why an authored value can leave this site, or null when it stays. Decided by the parser the
// browser uses on an href, because safeHttpUrl cannot answer it: that helper resolves the value
// only to READ the scheme and returns the original string, so `//evil.example.com/x` reports
// https and passes. Runs AFTER safeHttpUrl, so every value here is already http(s).
//
// A prefix test is the wrong instrument (measured 2026-08-11, 8 bypasses): the parser strips
// tab/CR/LF from ANYWHERE in the string and leading C0 controls BEFORE parsing, so
// "/<tab>/evil.com" resolves to https://evil.com/ without ever looking like "//" at index 0. It
// lies the other way too — a special-scheme URL reads "\" as "/", so "\shop\build" is same-origin.
//
// The base must be the real site origin: a placeholder host silently allowlists itself and
// rejects the real `//www.directv.com/x`.
//
// Fixed here rather than inside safeHttpUrl because that helper's other callers (show-carousal
// into an href, dtv-media-modal into window.open) pass CMS `targeturl` with the same gap — still
// open, and widening the helper is its own change.
const siteBase = 'https://www.directv.com/uf/';
// `http:/shop/x`, `https:/shop/x`, `http:shop/x` — rejected on SHAPE, because for these the same
// string resolves to different places depending on the page's own scheme, which no fixed base can
// see. Measured on `https:/shop/x`: from an https base it is www.directv.com/shop/x, from an http
// base the host becomes `shop`. (`http:…` variants happen to resolve off-origin from either base,
// so those would be caught anyway; `https:…` is the one that needs the shape rule.)
const schemeWithoutAuthority = /^[a-z][a-z\d+.-]*:(?!\/\/)/i;
// Off-site is accepted only when the author typed a full `https://` URL — a visible decision no
// path typo can produce. BK kept this policy on 2026-08-11 rather than narrowing it to a host
// allowlist; it is pinned by a test in catalog-adapter.spec, and this regex is the line to move.
const authoredAbsolute = /^https:\/\//i;
export const offSiteReason = (url: string): string | null => {
  if (schemeWithoutAuthority.test(url)) {
    return 'a scheme with no "//" — where it lands depends on the scheme of the page it is clicked from';
  }
  const base = new URL(siteBase);
  let resolved: URL;
  try {
    resolved = new URL(url, base);
  } catch {
    // Unreachable today (safeHttpUrl already parsed the same value; 0 of 3,782 fuzz candidates
    // reached here, 2026-08-11) but kept: its absence turns a parser edge case into a 500.
    return 'it cannot be parsed as a URL';
  }
  if (resolved.origin === base.origin || authoredAbsolute.test(url)) {
    return null;
  }
  return `it resolves to ${resolved.origin}`;
};

/**
 * Second results CTA destination (the recommender blok's `secondary_cta_*_url`), validated once
 * so a fixture or a second adapter cannot skip a check the Storyblok adapter enforces. `trimmed`
 * must already be non-empty — blank means "no second CTA", which callers handle before this runs.
 */
export const secondaryCtaUrl = (trimmed: string): { url: string; reason: null } | { url: null; reason: string } => {
  // trim() leaves controls and format characters (zero-width space, word joiner), and the parser
  // strips them to nothing — which would author a button pointing at the page it sits on.
  if (!/[^\p{Cc}\p{Cf}\s]/u.test(trimmed)) {
    return {
      url: null,
      reason: 'has no visible characters — dropped, no second CTA renders'
    };
  }
  const safe = safeHttpUrl(trimmed);
  if (!safe) {
    return {
      url: null,
      reason: `is not an http(s) or relative URL ("${trimmed}") — dropped, no second CTA renders`
    };
  }
  const offSite = offSiteReason(safe);
  if (offSite) {
    return {
      url: null,
      reason: `must stay on this site ("${safe}" — ${offSite}) — dropped, no second CTA renders; a URL on this site starts with a single "/"`
    };
  }
  // A path-relative value lands somewhere different on every page it is clicked from.
  if (!/^https?:\/\//i.test(safe) && !/^\/(?!\/)/.test(safe)) {
    return {
      url: null,
      reason: `"${trimmed}" is relative to the page it sits on — dropped, no second CTA renders; a URL on this site starts with a single "/"`
    };
  }
  return {
    url: safe,
    reason: null
  };
};

const SPANISH_SIGNATURE_ID = 'ATT_TV_OPTIMO_MAS';
const SPANISH_GENRE_IDS = ['ATT_TV_MIESPANOL'];
// Names must match the live epoch embedded-SVOD names (verified against the
// dtviEmbeddedSvodOffers cache, 2026-07-20 — 'Max' is now 'HBO Max Standard').
// Matching is any-overlap, so a single future rename degrades gracefully, but
// re-sync this list whenever epoch renames a premium service.
const PREMIUM_MOVIE_SVODS = ['STARZ', 'Paramount+ with SHOWTIME', 'HBO Max Standard'];
const SIGNATURE_SVODS: Record<string, string[]> = { ATT_TV_PREMIER: [...PREMIUM_MOVIE_SVODS] };
// Sports depth (RSNs etc.) starts at Choice (verified against rsnTierLegalTextObj.stream
// in the spike) — the liveSports:very floor anchor. Local team itself is out of scope
// (2026-07-21); only this structural fact survives.
const LIVE_SPORTS_MIN_SIGNATURE_ID = 'ATT_TV_CHOICE';

const toNumber = (v: number | string | null | undefined): number | null => {
  if (v == null || v === '') {
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// NFD so an accented letter becomes base + combining mark and the mark can be dropped, leaving the
// letter: without it the filter below deletes the whole character and 'ÓPTIMO MÁS™' slugs to
// `ptimo-ms`, a live 404 (measured on dev1 2026-08-07). NFD not NFKD — NFKD expands the '™' to a
// literal "tm" and yields `optimo-mastm`.
//
// Scope: combining marks only, which covers Spanish and Portuguese (á é í ó ú ñ ü ã õ ç). Letters
// with no canonical decomposition (œ æ ß ø ł) still get dropped whole — this is not a general
// non-ASCII fix. Two labels differing only by a diacritic now collide on one slug; adaptCatalog
// warns when that happens (see slugOwners).
const slugify = (label?: string): string => (label || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-');

export function adaptCatalog(sources: CatalogSources): AdaptedCatalog {
  const messages: RecipeValidationMessage[] = [];
  const entries = (sources.packageConfig || []).filter(e => e && e.id);
  if (entries.length === 0) {
    messages.push({
      level: 'error',
      message: 'packages config is empty or unavailable — no catalog can be built'
    });
    return {
      catalog: null,
      messages
    };
  }

  const rawPrices: Record<string, number | null> = {};
  const rawRegulars: Record<string, number | null> = {};
  const rawPromoMonths: Record<string, number | null> = {};
  const rawNextTiers: Record<string, NextPriceTier | null> = {};
  const rawPromoKnown: Record<string, boolean> = {};
  (sources.offerPrices || []).forEach(entry => {
    Object.keys(entry || {}).forEach(id => {
      rawPrices[id] = toNumber(entry[id] && entry[id].price);
      rawRegulars[id] = toNumber(entry[id] && entry[id].regularPrice);
      rawPromoMonths[id] = toNumber(entry[id] && entry[id].promoDuration);
      rawNextTiers[id] = (entry[id] && entry[id].nextTier) || null;
      rawPromoKnown[id] = (entry[id] && entry[id].promoKnown) !== false;
    });
  });
  const priceOf = (id: string): number | null => {
    const p = rawPrices[id];
    if (p == null) {
      return null;
    }
    if (p <= 0) {
      messages.push({
        level: 'warn',
        message: `package ${id}: non-positive live price (${p}) treated as unknown — no $0 cards`
      });
      return null;
    }
    return p;
  };

  // Prototype-less: the keys are CMS-authored strings, so `{}` would answer `byId['__proto__']`
  // and `byId['constructor']` with an inherited member. Measured 2026-08-11 — an authored
  // pack_code of `__proto__` passed the "is this a real package" check on the strength of
  // Object.prototype, then printed `[object Object]` into an author-facing warning. Nothing is
  // written through such a key and the prototype is never polluted, but the diagnostics lie.
  const byId: Record<string, PackageConfigEntry> = Object.create(null);
  entries.forEach(e => {
    byId[e.id as string] = e;
  });

  // Authored CTA URLs, resolved once: pack_code -> validated URL. Display-only, like `channels` —
  // the engine never reads `ctaUrl`, so authoring a row cannot move a recommendation.
  //
  // Validation lives here, not in the CMS adapter, because this is the only place a `ctaUrl` is
  // produced: a fixture or a second CMS adapter gets the same checks on the way to an `<a href>`.
  // Every guard below assumes NOTHING about a row's shape (CtaUrlOverride's fields are `unknown`).
  const textOf = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  // Prototype-less like byId above: keyed by authored text, and the duplicate check is a bare lookup.
  const ctaUrlByPackCode: Record<string, string> = Object.create(null);
  (Array.isArray(sources.ctaOverrides) ? sources.ctaOverrides : []).forEach((row, index) => {
    // A row that is not a blok (bare string, number, nested list) is what a half-migrated bloks
    // field looks like, and it would otherwise vanish: every field read off it is undefined, which
    // is indistinguishable from an empty row. Arrays need naming explicitly — `typeof [] ===
    // 'object'`. The index is in the message because there is no pack_code or URL to name the row by.
    if (row && (typeof row !== 'object' || Array.isArray(row))) {
      messages.push({
        level: 'warn',
        message: `cta url row ${index + 1} is not a blok (${Array.isArray(row) ? 'array' : typeof row}) — row ignored`
      });
      return;
    }
    const packCode = textOf(row && row.packCode);
    const rawUrl = textOf(row && row.ctaUrl);
    // Present but not text is a BROKEN field, not an unset one: "left blank" is normal, "something
    // upstream put a blok here" is not. Both fields get it, or a non-text pack_code reports as
    // "no pack_code".
    const isPresentButNotText = (v: unknown): boolean => v != null && typeof v !== 'string';
    const urlIsUnusable = Boolean(row) && isPresentButNotText(row.ctaUrl);
    const packCodeIsUnusable = Boolean(row) && isPresentButNotText(row.packCode);
    // A row the author added and never filled in is not a mistake — Storyblok ships every
    // untouched text field as ''. Only a half-filled row says something went wrong.
    if (!packCode && !rawUrl && !urlIsUnusable && !packCodeIsUnusable) {
      return;
    }
    if (urlIsUnusable) {
      messages.push({
        level: 'warn',
        message: `cta url for ${packCode || '(a row with no usable pack_code)'} is not a text value — row ignored`
      });
      return;
    }
    if (packCodeIsUnusable) {
      messages.push({
        level: 'warn',
        // A broken pack_code does not imply a URL — quoting an empty one reads as `cta url ""`.
        message: `${rawUrl ? `cta url "${rawUrl}"` : `cta url row ${index + 1}`} has a pack_code that is not a text value — row ignored`
      });
      return;
    }
    if (!packCode) {
      messages.push({
        level: 'warn',
        message: `cta url "${rawUrl}" is authored with no pack_code — row ignored`
      });
      return;
    }
    // Blank URL on a named product is the documented way to say "use the built-in URL", so it
    // is silent by design — unlike the cases below, nothing about it is wrong.
    if (!rawUrl) {
      return;
    }
    if (!byId[packCode]) {
      messages.push({
        level: 'warn',
        message: `cta url pack_code ${packCode} is not in packages config — ignored (the id must match a package exactly, e.g. ATT_TV_MYSPORTS)`
      });
      return;
    }
    // Nothing visible in it is a broken paste, and it does NOT fall out of the blank check above:
    // trim() removes tab/VT/FF but leaves the other C0 controls, so `"\0"` is non-empty here while
    // the parser strips it to nothing — that authored a Shop button pointing at the CURRENT page
    // (measured 2026-08-11). Same \p{Cc}\p{Cf} check as secondaryCtaUrl, so a zero-width space
    // alone is caught here too.
    if (!/[^\p{Cc}\p{Cf}\s]/u.test(rawUrl)) {
      messages.push({
        level: 'warn',
        message: `cta url for ${packCode} has no visible characters — dropped, the built-in URL is used instead`
      });
      return;
    }
    // Scheme first, so the destination check below never has to explain `javascript:`.
    const safe = safeHttpUrl(rawUrl);
    if (!safe) {
      messages.push({
        level: 'warn',
        message: `cta url for ${packCode} is not an http(s) or relative URL ("${rawUrl}") — dropped, the built-in URL is used instead`
      });
      return;
    }
    // Then the destination. Same outcome as a bad scheme, different cause, so it gets its own
    // wording — safeHttpUrl reports every one of these values as https.
    const offSite = offSiteReason(safe);
    if (offSite) {
      messages.push({
        level: 'warn',
        message: `cta url for ${packCode} must stay on this site ("${safe}" — ${offSite}) — dropped, the built-in URL is used instead; a URL on this site starts with a single "/"`
      });
      return;
    }
    // Last, so a broken second row is told what is wrong with IT rather than called a duplicate.
    // Compares against what was ACCEPTED, so a rejected first row does not block a valid second one.
    if (ctaUrlByPackCode[packCode]) {
      messages.push({
        level: 'warn',
        message: `cta url pack_code ${packCode} is authored more than once — keeping "${ctaUrlByPackCode[packCode]}" and ignoring "${rawUrl}"`
      });
      return;
    }
    ctaUrlByPackCode[packCode] = safe;
  });

  // Strikethrough price is display-only: kept when positive, never validated
  // against price here (the UI only renders it when regular > promo).
  const regularOf = (id: string): number | null => {
    const r = rawRegulars[id];
    return r != null && r > 0 ? r : null;
  };

  // How many months the live price holds. Display-only: the engine never reads it,
  // which is why it rides an extension of CatalogPackage instead of the engine type.
  // Integers only — a fractional periodMo would print "for first 1.5 mos".
  //
  // KNOWN DIVERGENCE (2026-07-29): the two upstream caches do not agree on what this
  // number means. The dtvi bridge takes the SHORTEST expiring tier (when the shown
  // price stops holding, matching plan-card-adapter's first-tier convention), while
  // epoch-genre-offers.resolver.ts:96-98 reduces to the LONGEST month-benefit. A genre
  // pack carrying more than one month-benefit would therefore overstate its window.
  // Not fixable here — that resolver feeds plan-cards and flip-cards too, so changing
  // it needs its own ticket; the genre offers live today ship a single tier.
  const promoMonthsOf = (id: string): number | null => {
    const m = rawPromoMonths[id];
    return m != null && m > 0 && Number.isInteger(m) ? m : null;
  };

  // Display-only like promoDuration. Dropped whole unless all three fields are
  // usable numbers and the window is real (start <= end) — a half-formed tier would
  // render a price disclosure with a missing figure, which is worse than silence.
  // Also dropped without a live promo window: the second line reads "then $X…", which
  // only means anything after a stated first window. Without that gate a rejected
  // promoDuration (0, fractional) would put "$94.99/mo. + tax" directly above
  // "then $84.99/mo. for mos 2-24" — two contradictory rates.
  const nextTierOf = (id: string): ResolvedPriceTier | null => {
    const t = rawNextTiers[id];
    const promoMonths = promoMonthsOf(id);
    if (!t || promoMonths == null) {
      return null;
    }
    const price = toNumber(t.price);
    const startMo = toNumber(t.startMo);
    const endMo = toNumber(t.endMo);
    // The window must also CONTINUE the first one: the card prints "for first N mos, then
    // $X/mo. for mos S-E", so S !== N+1 leaves months unaccounted for on price copy. The
    // benefits-derived path satisfies this by construction (startMo = expiresFirst + 1);
    // only a bridge feed supplying promoDuration and nextTier independently can break it.
    // promoMonths is always a positive integer (promoMonthsOf), so the continuation check
    // subsumes startMo's positivity/integrality; the null test stays for narrowing.
    const badWindow = startMo == null || startMo !== promoMonths + 1 ||
      endMo == null || endMo < startMo || !Number.isInteger(endMo);
    if (price == null || price <= 0 || badWindow) {
      // A tier arrived but is unusable — the upstream feed contradicts itself, which is
      // louder than the routine "no tier at all" nulls above (those stay silent).
      messages.push({
        level: 'warn',
        message: `package ${id}: nextTier dropped — needs a positive price and an integer window continuing promoDuration ${promoMonths} (got price ${t.price}, mos ${t.startMo}-${t.endMo})`
      });
      return null;
    }
    return {
      price,
      startMo,
      endMo
    };
  };

  // "+ tax" asserts the shown price is what the customer keeps paying, so it may only be
  // printed where the source positively established there is no expiring discount. Two
  // ways that fails: the bridge said so outright (promoKnown false), or a struck price
  // proves a discount exists while no usable window came with it — epoch-genre-offers
  // .resolver.ts:96-98 only counts month-period benefits, so a genre pack discounted any
  // other way arrives with regularPrice set and promoDuration null. Printing "+ tax"
  // under a strikethrough would have the same card say the price is both promotional and
  // permanent; the bare "/mo." claims neither.
  const promoKnownOf = (id: string): boolean => {
    if (rawPromoKnown[id] === false) {
      return false;
    }
    const price = priceOf(id);
    const regular = regularOf(id);
    const discounted = price != null && regular != null && regular > price;
    return !(discounted && promoMonthsOf(id) == null);
  };

  // Display only — the engine never reads `channels`, so an entry gaining or losing it
  // cannot move a recommendation. Everything is optional: a product with no authored
  // channel block yields null and the card simply omits the line.
  //
  // Deliberately silent rather than pushing a `warn` like the price/queryparam gaps do:
  // the channel line is decorative, it is legitimately absent on some products (MyHome
  // Team live today), and one message per unauthored product would drown the messages
  // array that the component uses to decide whether the config is usable at all.
  //
  // `IChannelConfig` (lib/types/global.ts) types all four fields as `string`, so an
  // unauthored Storyblok text field arrives as `""` rather than undefined — hence `||`
  // over `??` throughout, matching every other reader of this field in the repo
  // (plan-card-adapter, package-compare-tool-adapeter, genre-preview-tool-adapter,
  // custom-acq-compare-tool/utils/getChannels).
  const channelsOf = (e: PackageConfigEntry): string | null => {
    // Storyblok serialises a bloks field as an array; flip-cards-adapter.ts:304 reads
    // this same key and handles both shapes, so match it rather than silently
    // dropping the line when the CMS sends the array form.
    const c = Array.isArray(e.channel) ? e.channel[0] : e.channel;
    if (!c) {
      return null;
    }
    // Renderable scalars only. A hand-authored or partially-migrated feed can put an
    // object or a nested blok here, and a bare String() would print
    // "[object Object]+ channels" onto a live card. Numbers additionally have to clear
    // the same bar as price: NaN/Infinity/0/negatives are not channel counts, and
    // String(NaN) is truthy, so a typeof check alone would ship "NaN+ channels".
    const text = (v: unknown): string => {
      if (typeof v === 'number') {
        return Number.isFinite(v) && v > 0 ? String(v) : '';
      }
      return typeof v === 'string' ? v.trim() : '';
    };
    // `||`, not `??`: an unauthored Storyblok text field is '' rather than undefined.
    const n = text(c.displayCount) || text(c.count);
    if (!n) {
      return null;
    }
    const sign = text(c.signLetter);
    const label = text(c.label);
    // The noun stays a CMS string; without one the figure alone is not a sentence,
    // so there is nothing safe to render.
    return label ? `${n}${sign} ${label}` : null;
  };

  // Two labels that differ only by a diacritic now produce ONE slug, because slugify strips
  // combining marks. `slug` feeds both the derived CTA URL and the analytics item_id, so a collision
  // makes two products link to the same page and report the same id — silently, since neither is
  // wrong on its own. A warn rather than a comment claiming "no live label pair collides today":
  // that claim is about data this file cannot see, and it stops being true without anyone editing
  // code. Diagnostic only; nothing is dropped, because either product may legitimately be the one
  // the author meant to rename.
  const slugOwners: Record<string, string[]> = Object.create(null);
  entries.forEach(e => {
    const s = slugify(e.displayLabel);
    if (!s) {
      return;
    }
    (slugOwners[s] = slugOwners[s] || []).push(e.id as string);
  });
  Object.keys(slugOwners).forEach(s => {
    if (slugOwners[s].length > 1) {
      messages.push({
        level: 'warn',
        message: `display labels for ${slugOwners[s].join(', ')} all reduce to the slug "${s}" — their derived shop URLs and analytics ids collide; author a cta url for each, or make the labels differ by more than an accent`
      });
    }
  });

  const mkPkg = (e: PackageConfigEntry): PricedCatalogPackage => ({
    id: e.id as string,
    // 🔴 RAW on purpose, and it took an adversarial review to find out why. This
    // label is NOT display-only: it flows to `broadLabel` (engine.ts) → the card's
    // `title` → `handleResultCta(card.title, ...)` → the GA4 `select_item`
    // `item_id`/`item_name` at ai-product-recommender.tsx:2191-2192, which are
    // `title.toLowerCase()`. Decoding it would have silently renamed that event's
    // id from "&oacute;ptimo m&aacute;s" to "óptimo más" on deploy, breaking the
    // reporting series — while `view_item_list` kept the old slug-derived id
    // (`broadItemId(result.broad?.slug)`), so the two events would disagree.
    //
    // And it buys nothing on screen: the card renders the title through
    // `<DtvText content={title} />` (ai-product-recommender.tsx:1146), whose
    // `content` prop goes through html-react-parser, which decodes entities
    // itself. Measured: `parse('Max&#8482;')` → "Max™".
    //
    // `slug` below is raw for a third reason: slugify strips non-alphanumerics,
    // so decoding first would turn "directvreg-internet" into
    // "directv-internet" and move the derived shop CTA url too.
    label: (e.displayLabel || '').trim() || (e.id as string),
    price: priceOf(e.id as string),
    regularPrice: regularOf(e.id as string),
    promoDuration: promoMonthsOf(e.id as string),
    nextTier: nextTierOf(e.id as string),
    promoKnown: promoKnownOf(e.id as string),
    channels: channelsOf(e),
    shopCode: (e.queryparam || '').trim().toUpperCase() || undefined,
    slug: slugify(e.displayLabel) || undefined,
    // Keyed on the canonical id, never on the label: the label is what the derived slug got
    // wrong in the first place.
    ctaUrl: ctaUrlByPackCode[e.id as string] || undefined
  });

  // Live embedded-SVOD join (v2): svod names flow verbatim from the epoch caches,
  // so inclusion copy can only ever show what is actually embedded today. When the
  // cache is absent the structural SIGNATURE_SVODS fallback keeps premium-movie
  // matching working — inclusion copy shrinks to the Premier premium set instead
  // of fabricating a full lineup.
  const embedded = sources.embeddedSvodOffers || null;
  const liveSvodsOf = (e: PackageConfigEntry): string[] | null => {
    const list = embedded ? embedded[(e.queryparam || '').trim().toLowerCase()] : null;
    if (!Array.isArray(list)) {
      return null;
    }
    const names = list.map(o => decodeHtmlEntities(((o && o.name) || '').trim())).filter(Boolean);
    return names.length ? names : null;
  };
  if (!embedded || Object.keys(embedded).length === 0) {
    messages.push({
      level: 'warn',
      message: 'embedded SVOD cache (dtviEmbeddedSvodOffers) is unavailable — signature svods fall back to structural constants'
    });
  }

  const svodMapping = sources.genreSvodMapping || null;
  const svodNameByCode = sources.svodNameMapping || {};
  if (!svodMapping || Object.keys(svodMapping).length === 0) {
    messages.push({
      level: 'warn',
      message: 'genre SVOD mapping (genreSvodOffers.svodMapping) is unavailable — genre-pack svods will be empty'
    });
  } else if (Object.keys(svodNameByCode).length === 0) {
    messages.push({
      level: 'warn',
      message: 'genre SVOD name mapping (genreSvodOffers.svodNameMapping) is unavailable — genre-pack svods will be empty'
    });
  }
  const genreSvodsOf = (id: string): string[] => {
    const codes = svodMapping ? svodMapping[id] : null;
    if (!Array.isArray(codes)) {
      return [];
    }
    return codes.map(c => decodeHtmlEntities((svodNameByCode[c] || '').trim())).filter(Boolean);
  };

  // Signatures are selected by canonical id — the `type` value of signature
  // entries is not relied on. Unpriced tiers are excluded from the ladder so
  // the broad card can never surface without a live price.
  const signatures: PricedCatalogPackage[] = [];
  Object.keys(SIGNATURE_RANKS).forEach(id => {
    const entry = byId[id];
    if (!entry) {
      messages.push({
        level: 'warn',
        message: `signature ${id} is not in packages config — excluded from the ladder`
      });
      return;
    }
    const pkg: PricedCatalogPackage = {
      ...mkPkg(entry),
      rank: SIGNATURE_RANKS[id],
      svods: liveSvodsOf(entry) || SIGNATURE_SVODS[id] || []
    };
    if (pkg.price == null) {
      messages.push({
        level: 'warn',
        message: `signature ${id} has no live price — excluded from the ladder`
      });
      return;
    }
    signatures.push(pkg);
  });
  if (signatures.length === 0) {
    messages.push({
      level: 'error',
      message: 'no signature package has a live price — the catalog is unusable'
    });
  }
  // The engine throws on a liveSports:very answer if the sports floor tier is absent.
  if (signatures.length > 0 && !signatures.some(s => s.id === LIVE_SPORTS_MIN_SIGNATURE_ID)) {
    messages.push({
      level: 'error',
      message: `live-sports floor signature (${LIVE_SPORTS_MIN_SIGNATURE_ID}) is missing or unpriced — the liveSports:very mapping would fail`
    });
  }

  let spanishSignature: PricedCatalogPackage | null = null;
  const spanishEntry = byId[SPANISH_SIGNATURE_ID];
  if (!spanishEntry) {
    messages.push({
      level: 'error',
      message: `spanish signature (${SPANISH_SIGNATURE_ID}) is not in packages config — the spanish lane would crash`
    });
  } else {
    spanishSignature = {
      ...mkPkg(spanishEntry),
      rank: 1,
      svods: liveSvodsOf(spanishEntry) || []
    };
    if (spanishSignature.price == null) {
      messages.push({
        level: 'error',
        message: `spanish signature (${SPANISH_SIGNATURE_ID}) has no live price`
      });
    }
  }

  const genrePacks: PricedCatalogGenrePack[] = entries
    .filter(e => e.type === 'genre' || e.type === 'genre-mini')
    .map(e => ({
      ...mkPkg(e),
      mini: e.type === 'genre-mini',
      group: SPANISH_GENRE_IDS.includes(e.id as string) ? 'spanish' : 'genre',
      svods: genreSvodsOf(e.id as string)
    }));
  if (genrePacks.length === 0) {
    messages.push({
      level: 'error',
      message: 'packages config contains no genre packs — the focused card cannot be built'
    });
  }
  genrePacks.forEach(g => {
    if (g.price == null) {
      messages.push({
        level: 'warn',
        message: `genre pack ${g.id} has no live price — it can never be the focused candidate and the UI must not show it priceless`
      });
    }
    if (!g.shopCode) {
      messages.push({
        level: 'warn',
        message: `genre pack ${g.id} has no queryparam — the configure CTA cannot be built for it`
      });
    }
  });

  const catalog: PricedCatalog = {
    signatures,
    premiumMovieSvods: PREMIUM_MOVIE_SVODS,
    spanishSignature: spanishSignature || {
      id: SPANISH_SIGNATURE_ID,
      label: SPANISH_SIGNATURE_ID,
      price: null
    },
    genrePacks,
    liveSports: { minSignatureId: LIVE_SPORTS_MIN_SIGNATURE_ID }
  };
  return {
    catalog,
    messages
  };
}

// Cross-checks an adapted recipe against the live catalog and degrades
// gracefully: a theme or combo pointing at a pack that is not sellable today is
// dropped (its option effects become engine no-ops).
// Everything here is a warn — there is no fallback pack left to guard, so nothing in this
// function can blank the component. The engine holds up its half of the "no-op" contract by
// requiring a themes-table row before a name counts as a coverage theme (engine.ts, English
// lane); it once kept the orphan name and lost the focused card instead.
// Run this AFTER adaptRecipe/validateRecipe; do not re-validate afterwards
// (dropped themes would double-report as unknown theme effects).
export function crossCheckRecipeCatalog(
  recipe: Recipe,
  catalog: Catalog
): { recipe: Recipe; messages: RecipeValidationMessage[] } {
  const messages: RecipeValidationMessage[] = [];
  const known = new Set<string>([
    catalog.spanishSignature.id,
    ...catalog.signatures.map(s => s.id),
    ...catalog.genrePacks.map(g => g.id)
  ]);
  const genrePackIds = new Set<string>(catalog.genrePacks.map(g => g.id));

  // A packId that exists but is NOT a genre pack passes the engine's focused-candidate
  // eligibility (isMini only inspects genrePacks) yet buildFocusedCtaUrl only searches
  // genrePacks — so if it wins, the card renders with a price and no Shop button. Warn-only:
  // filtering the row would change recommendations, and that is a rule change, not a check.
  // Spanish-flip theme rows are exempt — they legitimately point at the Spanish Signature
  // and are excluded from focused candidates anyway.
  const warnNonGenrePack = (what: string, packId: string) => {
    if (known.has(packId) && !genrePackIds.has(packId)) {
      messages.push({
        level: 'warn',
        message: `${what}: pack ${packId} is a Signature, not a Genre Pack — if it becomes the focused candidate its card renders with no Shop CTA`
      });
    }
  };

  const themes = recipe.themes.filter(t => {
    if (t.name && t.packId && !known.has(t.packId)) {
      messages.push({
        level: 'warn',
        message: `theme "${t.name}": pack ${t.packId} is not in the live catalog — theme disabled`
      });
      return false;
    }
    if (t.name && t.packId && !t.spanishFlip) {
      warnNonGenrePack(`theme "${t.name}"`, t.packId);
    }
    return true;
  });

  const combos = (recipe.combos || []).filter(c => {
    if (c.packId && !known.has(c.packId)) {
      messages.push({
        level: 'warn',
        message: `combo pack ${c.packId} is not in the live catalog — combo disabled`
      });
      return false;
    }
    if (c.packId) {
      // no spanishFlip exemption here on purpose: a combo competes only on English coverage
      // themes (>=2), so a Signature packId on a combo row is mis-authoring in every case
      warnNonGenrePack('combo', c.packId);
    }
    return true;
  });

  // The default-focused-pack cross-check is gone with the field itself — the 2026-07-24 rules
  // have no fallback pack, so there was nothing left for it to protect (see recipe-adapter).
  return {
    recipe: {
      ...recipe,
      themes,
      combos
    },
    messages
  };
}
